import React, { FC, ReactElement, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { lighten, Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import AdyenCheckout from '@adyen/adyen-web';
import * as Sentry from '@sentry/react';
import { clsx } from 'clsx';
// Components
import PageContainer from '../../../components/layout/pageContainer';
import BackButton from '../../../components/buttons/backButton';
import Loader from '../../../components/loader';
import Text from '../../../components/text';
import Space from '../../../components/layout/space';
// Adyen
import '@adyen/adyen-web/dist/adyen.css';
// import callServer from './serverApi';
import { createCheckoutConfig } from './utils';
import { ToastWithIcon } from '../toastProvider';
import { useCallDropin } from '../../../core/services/payments';
// Resources
import { stringsRes } from '../../../resources/strings';
import { DropinInput } from '../../../core/apiTypes';
import { paths } from '../paths';
import { WebLocation, useBreakpoints } from '../../../customHooks/useBreakpoints';

const DropInAdyenPage: FC = (): ReactElement => {
    const { isMobileSize } = useBreakpoints(WebLocation.FLOW);

    return (
        <PageContainer title={isMobileSize ? stringsRes.booking.paymentTitle : undefined}>
            <DropInAdyen />
        </PageContainer>
    );
};

interface DropinAdyenProps {
    contractId?: string;
    selfService?: boolean;
    inFlow?: boolean;
    inOnboarding?: boolean;
    skinny?: boolean;
    onSuccess?: () => void;
    onError?: () => void;
}

export const DropInAdyen: FC<DropinAdyenProps> = ({
    selfService = false,
    inFlow = false,
    inOnboarding = false,
    skinny = false,
    contractId: overrideContractId,
    onSuccess,
    onError,
}): ReactElement => {
    const classes = useStyles();
    const { isMobileSize } = useBreakpoints(WebLocation.FLOW);

    const navigate = useNavigate();
    const location = useLocation();
    const paymentContainer = useRef(null);
    const contractId = overrideContractId ?? location.state?.contractId;

    const [checkoutSesssion, setCheckoutSesssion] = useState<any>();

    // Shown when the drop-in has completed but the navigation away didn't take effect (observed
    // after the in-page 3DS challenge): the drop-in has cleared its UI and the page would otherwise
    // stay blank, so we invite the user to go back manually with the top-left arrow.
    const [showFallback, setShowFallback] = useState(false);
    const fallbackTimer = useRef<ReturnType<typeof setTimeout>>();

    const isMounted = useRef(true);
    // Where the drop-in was mounted. Sentry stamps events with the URL at capture time, and these
    // failures are captured from async callbacks that can outlive a navigation — without this the
    // report points at whichever page the app had moved on to.
    const mountRoute = useRef(location.pathname);

    const createDropin = useCallDropin();

    const showBackButton: boolean = !selfService && !inFlow && !inOnboarding;

    const armFallback = (): void => {
        fallbackTimer.current = setTimeout(() => setShowFallback(true), 2000);
    };

    // Mark us gone, and clear the fallback timer if we do leave the page (navigation succeeded)
    // before it fires.
    useEffect(
        () => () => {
            isMounted.current = false;
            clearTimeout(fallbackTimer.current);
        },
        [],
    );

    // The drop-in could not be loaded (session creation or mount failed). There is no drop-in to
    // recover into, so we must surface an error and leave the page instead of staying on a loader.
    const handleLoadError = (context: string, e: any): void => {
        // These callbacks can resolve after we are gone — the onboarding modal swaps its step, or
        // the user navigates. Toasting and navigating from a dead component is worse than silence.
        if (!isMounted.current) {
            return;
        }
        console.log(`Error ${context}:`, e);
        // Errors go through message: JSON.stringify on an Error yields "{}", since message and
        // stack are not enumerable. Anything else (an API error payload) still stringifies.
        const details = e?.message ?? (e ? JSON.stringify(e) : '');
        Sentry.withScope((scope) => {
            scope.setTag('adyen.mount_route', mountRoute.current);
            Sentry.captureMessage(`[Adyen] ${context} error: ${details}`, Sentry.Severity.Debug);
        });
        ToastWithIcon('error', stringsRes.payment.errors.error);
        if (inOnboarding) {
            onError?.();
        } else if (selfService) {
            navigate(paths.selfServicePaymentSettings);
        } else if (inFlow) {
            navigate(paths.homeService);
        } else {
            navigate(contractId ? paths.adminPayment : paths.payment);
        }
    };

    //= ========================================//
    //= ============== UseEffect ===============//
    //= ==========================:=============//

    useEffect(() => {
        const payload: DropinInput = {
            url: '/payment-methods/adyen/sessions',
        };

        createDropin.mutate(payload, {
            onSuccess: (res: any) => {
                setCheckoutSesssion(res);
            },
            onError: (e) => {
                handleLoadError('Create session', e);
            },
        });
        // callServer('/payment-methods/adyen/sessions')
        //     .then((res) => {
        //         setCheckoutSesssion(res);
        //     })
        //     .catch((e) => {
        //         console.log('Error Create session:', e);
        //     });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!checkoutSesssion || !paymentContainer.current) {
            // initiateCheckout is not finished yet.
            return;
        }

        createCheckoutConfig(
            checkoutSesssion,
            createDropin,
            (data: any) => {
                // OnSuccess
                console.log({ data, selfService, inFlow });
                if (selfService) {
                    navigate(paths.selfServicePaymentSettings);
                } else if (inFlow) {
                    navigate(paths.homeService);
                } else if (inOnboarding) {
                    onSuccess?.();
                    return;
                } else {
                    navigate(contractId ? paths.adminPayment : paths.payment);
                }
                armFallback();
            },
            () => {
                // OnError
                if (inOnboarding) {
                    onError?.();
                    return;
                }
                if (!selfService && !contractId) {
                    navigate(paths.payment);
                }
            },
            !!contractId,
            contractId,
        )
            .then((config) => {
                localStorage.setItem('adyenConfig', JSON.stringify({ ...config, isPro: !!contractId, contractId }));
                return AdyenCheckout(config);
            })
            // Create an instance of Drop-in and mount it
            .then((checkout) => {
                // Two awaits happened since the guard above, and React nulls the ref on unmount.
                // Adyen's mount() throws "Root node was not found" on a falsy node, which is not a
                // payment failure — there is simply no longer a page to draw on.
                const node = paymentContainer.current;
                if (!node) {
                    return null;
                }
                return checkout
                    .create('dropin', {
                        // Possibilité de paramétrer le dropin ici aussi (fonctionnalités, display, etc..)
                    })
                    .mount(node);
            })
            .catch((e) => {
                handleLoadError('Checkout', e);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkoutSesssion]);

    //= =====================================//
    //= ============== Render ===============//
    //= =====================================//

    return (
        <>
            {showBackButton && (
                <>
                    {!isMobileSize && (
                        <BackButton
                            label={stringsRes.booking.paymentTitle}
                            title={stringsRes.payment.paymentAdd}
                            spaceBottom={1}
                        />
                    )}
                </>
            )}
            {!checkoutSesssion && <Loader color="secondary" text={stringsRes.common.loading} />}
            {showFallback && (
                <div>
                    <Text.H1 text={stringsRes.payment.redirectDoneTitle} />
                    <Space value={1} />
                    <Text.ShortRegularText16 text={stringsRes.payment.redirectDoneHint} />
                </div>
            )}
            <div className="payment-container" style={showFallback ? { display: 'none' } : undefined}>
                <div ref={paymentContainer} className={clsx(classes.paymentContainer, skinny && classes.skinny)} />
            </div>
        </>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        paymentContainer: {
            fontSize: 14,
            fontFamily: 'Roboto',
            '& .adyen-checkout__button--pay': {
                backgroundColor: lighten(theme.palette.secondary.light, 0.1),
                fontWeight: 700,
            },
            '& .adyen-checkout__button:hover': {
                backgroundColor: theme.palette.secondary.light,
            },
            '& .adyen-checkout__payment-method__header__title': {
                display: 'none',
            },
            '& .adyen-checkout__payment-method__header ': {
                padding: '12px 16px 12px 44px !important',
            },
            '& .adyen-checkout__payment-method--selected': {
                backgroundColor: 'transparent',
                border: 'unset',
            },
            '& .adyen-checkout__payment-method__details': {
                padding: 0,
            },
            '& .adyen-checkout__label__text': {
                color: theme.palette.primary.main,
            },
            '& .adyen-checkout__error-text': {
                color: theme.palette.colors.text.error,
            },
            '& .adyen-checkout-form-instruction': {
                color: theme.palette.colors.text.label,
            },
        },
        skinny: {
            '& .adyen-checkout__payment-method__header': {
                padding: '0 !important',
            },
            '& .adyen-checkout__button--pay': {
                marginBottom: '0 !important',
            },
        },
    }),
);

export default DropInAdyenPage;
